import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Users, TrendingUp, AlertTriangle, Plus, Trash2, MessageCircle } from "lucide-react";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user } = useApp();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const emptyProd = { name: "", description: "", price: 0, original_price: 0, category: "men", subcategory: "shirts", images: [""], sizes: "S,M,L,XL", colors: "", stock: 10, featured: false };
  const [prod, setProd] = useState(emptyProd);
  const emptyCoupon = { code: "", discount_percent: 0, discount_flat: 0, min_order: 0, active: true, description: "" };
  const [coupon, setCoupon] = useState(emptyCoupon);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders/all").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/coupons/all").then((r) => setCoupons(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    load();
  }, [user, nav]);

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...prod, price: +prod.price, original_price: +prod.original_price || null, stock: +prod.stock,
        images: prod.images.filter(Boolean),
        sizes: prod.sizes.split(",").map(s => s.trim()).filter(Boolean),
        colors: prod.colors ? prod.colors.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      await api.post("/products", body);
      toast.success("Product added"); setProd(emptyProd); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delProduct = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/products/${id}`); load(); };
  const updateOrder = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Order → ${status}`); load();
    } catch { toast.error("Failed"); }
  };
  const setTracking = async (id) => {
    const t = prompt("Tracking number:");
    if (!t) return;
    await api.patch(`/orders/${id}/status`, { status: "shipped", tracking: t });
    toast.success("Tracking added"); load();
  };
  const addCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", { ...coupon, discount_percent: +coupon.discount_percent, discount_flat: +coupon.discount_flat, min_order: +coupon.min_order });
      toast.success("Coupon created"); setCoupon(emptyCoupon); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delCoupon = async (id) => { await api.delete(`/coupons/${id}`); load(); };

  if (!user || user.role !== "admin") return null;

  const wa = (o) => `https://wa.me/${(o.address.phone || "").replace(/\D/g,"")}?text=${encodeURIComponent(`Hi ${o.customer_name}, this is Venvay. Update on your order #${o.order_number}: status is "${o.status.toUpperCase()}". Track: ${o.tracking || "-"}. Thank you!`)}`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-tag">Command Center</p>
          <h1 className="font-display text-4xl font-black">Admin Dashboard</h1>
        </div>
        <span className="text-xs text-muted-foreground">{user.email}</span>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { label: "Revenue", value: inr(stats.revenue), icon: TrendingUp, color: "text-[#8B2E2E]" },
            { label: "Orders", value: stats.total_orders, icon: Package, color: "text-[#0A0A0A]" },
            { label: "Pending", value: stats.pending_orders, icon: AlertTriangle, color: "text-amber-700" },
            { label: "Customers", value: stats.customers, icon: Users, color: "text-blue-700" },
          ].map((s) => (
            <div key={s.label} className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-5">
              <div className="flex items-center justify-between">
                <p className="label-tag">{s.label}</p>
                <s.icon size={16} className={s.color} />
              </div>
              <p className="font-display text-3xl font-black mt-2 text-[#D4B25C]" data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-10">
        <TabsList>
          <TabsTrigger data-testid="tab-admin-orders" value="orders">Orders</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-products" value="products">Products & Stock</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-coupons" value="coupons">Coupons</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="space-y-3">
            {orders.length === 0 && <p className="text-center text-muted-foreground py-10">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-mono font-semibold">#{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{o.customer_name} · {o.customer_email}</p>
                  <p className="text-xs text-muted-foreground">{o.items.length} items · {new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">{inr(o.total)}</p>
                  <p className="text-xs text-muted-foreground">{o.payment_method.toUpperCase()}</p>
                </div>
                <Select value={o.status} onValueChange={(v) => updateOrder(o.id, v)}>
                  <SelectTrigger data-testid={`status-${o.order_number}`} className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => setTracking(o.id)} data-testid={`track-${o.order_number}`}>Track</Button>
                <a href={wa(o)} target="_blank" rel="noreferrer" data-testid={`wa-${o.order_number}`}>
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#1fa855] text-white"><MessageCircle size={14} /></Button>
                </a>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6 space-y-8">
          <form onSubmit={addProduct} className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-6">
            <p className="label-tag mb-4">Add Product</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input required value={prod.name} onChange={(e) => setProd({ ...prod, name: e.target.value })} data-testid="prod-name" /></div>
              <div><Label>Image URL</Label><Input required value={prod.images[0]} onChange={(e) => setProd({ ...prod, images: [e.target.value] })} data-testid="prod-image" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea value={prod.description} onChange={(e) => setProd({ ...prod, description: e.target.value })} /></div>
              <div><Label>Price (₹)</Label><Input type="number" required value={prod.price} onChange={(e) => setProd({ ...prod, price: e.target.value })} data-testid="prod-price" /></div>
              <div><Label>Original Price (₹)</Label><Input type="number" value={prod.original_price} onChange={(e) => setProd({ ...prod, original_price: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <Select value={prod.category} onValueChange={(v) => setProd({ ...prod, category: v })}>
                  <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="men">Men</SelectItem><SelectItem value="women">Women</SelectItem><SelectItem value="gifts">Gifts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory</Label>
                <Select value={prod.subcategory} onValueChange={(v) => setProd({ ...prod, subcategory: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shirts">Shirts</SelectItem><SelectItem value="pants">Pants</SelectItem><SelectItem value="jeans">Jeans</SelectItem><SelectItem value="gifts">Gifts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Sizes (comma sep)</Label><Input value={prod.sizes} onChange={(e) => setProd({ ...prod, sizes: e.target.value })} /></div>
              <div><Label>Colors (comma sep)</Label><Input value={prod.colors} onChange={(e) => setProd({ ...prod, colors: e.target.value })} /></div>
              <div><Label>Stock</Label><Input type="number" value={prod.stock} onChange={(e) => setProd({ ...prod, stock: e.target.value })} /></div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={prod.featured} onChange={(e) => setProd({ ...prod, featured: e.target.checked })} /> Featured</label></div>
            </div>
            <Button type="submit" className="mt-4 bg-[#8B2E2E] hover:bg-[#6f2323]" data-testid="prod-add-btn"><Plus size={14} className="mr-1" /> Add Product</Button>
          </form>

          <div className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-4">
            <p className="label-tag mb-4">Inventory ({products.length})</p>
            <div className="space-y-2">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border-b border-white/10 pb-2">
                  <img src={p.images[0]} alt="" className="h-12 w-10 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.category} · {p.subcategory} · {inr(p.price)}</p>
                  </div>
                  <Badge className={`${p.stock < 5 ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>Stock: {p.stock}</Badge>
                  <button data-testid={`del-prod-${p.id}`} onClick={() => delProduct(p.id)} className="text-muted-foreground hover:text-[#8B2E2E] p-2"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="coupons" className="mt-6 space-y-6">
          <form onSubmit={addCoupon} className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-6 grid md:grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })} data-testid="coupon-code" /></div>
            <div><Label>Description</Label><Input value={coupon.description} onChange={(e) => setCoupon({ ...coupon, description: e.target.value })} /></div>
            <div><Label>Discount %</Label><Input type="number" value={coupon.discount_percent} onChange={(e) => setCoupon({ ...coupon, discount_percent: e.target.value })} /></div>
            <div><Label>Flat ₹ off</Label><Input type="number" value={coupon.discount_flat} onChange={(e) => setCoupon({ ...coupon, discount_flat: e.target.value })} /></div>
            <div><Label>Min Order ₹</Label><Input type="number" value={coupon.min_order} onChange={(e) => setCoupon({ ...coupon, min_order: e.target.value })} /></div>
            <div className="flex items-end"><Button type="submit" className="bg-[#8B2E2E] hover:bg-[#6f2323]" data-testid="coupon-add-btn"><Plus size={14} className="mr-1" /> Create</Button></div>
          </form>
          <div className="bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-4 space-y-2">
            {coupons.map((c) => (
              <div key={c.id} className="flex items-center gap-3 border-b border-white/10 pb-2">
                <div className="font-mono font-bold text-[#D4B25C]">{c.code}</div>
                <div className="flex-1 text-sm text-muted-foreground">{c.description || `${c.discount_percent}% + ₹${c.discount_flat} off`} · Min ₹{c.min_order}</div>
                <button onClick={() => delCoupon(c.id)} data-testid={`del-coupon-${c.code}`} className="text-muted-foreground hover:text-[#8B2E2E] p-2"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
